import { Box, Flex, Stack, Tooltip, Text, Menu } from "@mantine/core";
import {
  BagelAiIcon,
  FromBagelIcon,
  RequestIcon,
} from "../../../../icons/x-symbol-svgrepo-com";
import { SourceIcon } from "../../../../utils/SourceIcon";
import { IconAlertOctagon, IconCheck } from "@tabler/icons";
import { useContext, useEffect, useState } from "react";
import { saveRequest } from "../../../../Api";
import { useAuth0 } from "@auth0/auth0-react";
import { showNotification } from "@mantine/notifications";
import IdeaContext from "../../IdeaContext";
import { capitalizeFirstLetter } from "../../../../utils/capitalizeFirstLetter";
import { getNestedValue } from "../../Funcs";
import { formatBigMonetaryValueIdeas } from "../../../ideas/ideas-table-helpers/Helpers";
import { useGroupedAggregations } from "../../../../hooks/ideaPage/useIdeaQueries";
import {useFields} from "../../../../hooks/useFields";
import { resolveEvidenceDisplayOrigin } from "../../../../utils/evidence/evidenceDisplayOrigin";

const CardAttributes = ({ 
  item, 
  loadEvidence: propLoadEvidence 
}: { 
  item: any;
  loadEvidence?: () => void;
}) => {
  const auth0 = useAuth0();
  const { requestPriorities, allComponents, impactAggregationProperties } = useFields();

  const impactsToShow = impactAggregationProperties?.filter(
    (a: any) => a?.impact?.displayInEvidenceCard !== false
  ) || [];

  const contextData = useContext(IdeaContext);
  const { loadEvidence: contextLoadEvidence } = contextData;
  
  // Use prop if provided, otherwise use context
  const finalLoadEvidence = propLoadEvidence || contextLoadEvidence;

  const [priority, setPriority] = useState<string>(
    item?.priority || "Unassigned"
  );
  const [color, setColor] = useState("");

  const sourceOfAssignment = item?.sourceOfAssignment; // FIX sourceOfAssignment FOR MATCHES
  const origin = resolveEvidenceDisplayOrigin(item);
  const company = item?.company || null;
  const companyName = company?.name;

  const onSelectSave = (type: string, value: string) => {
    let updatedValue = value;

    if (type === "componentId") {
      const found = allComponents.find((a: any) => a._id === value);
      updatedValue = found?.name;
    }

    if (item) {
      const itemObj = {
        id: item?._id,
        [type]: value,
      };

      saveRequest(itemObj, auth0)
        .then(() => {
         
          finalLoadEvidence();
          showNotification({
            title: `${
              type === "componentId"
                ? "Product area"
                : type === "priority"
                  ? "Priority"
                  : "Domain"
            } updated`,
            color: "teal",
            message: `${
              type === "componentId"
                ? "Product area"
                : type === "priority"
                  ? "Priority"
                  : "Domain"
            } updated to "${updatedValue}" successfully`,
            icon: <IconCheck size="1.1rem" />,
            autoClose: 1200,
          });
        })
        .catch((e: any) => console.log(e));
      
    }
  };

  const [evidenceAggregations, setEvidenceAggregations] = useState<any>(null);
  
  const evidenceIds = item?.isContainer && item?.hasChildren ? [...(item?.childEvidences?.map((child: any) => String(child._id)) ?? []), String(item?._id)] : [];
  const aggregationsQuery = evidenceIds ? useGroupedAggregations(evidenceIds) : null;
  
  useEffect(() => {
    if (aggregationsQuery && aggregationsQuery?.data?.aggregations ) {
      setEvidenceAggregations(aggregationsQuery?.data?.aggregations[0] || null );
    }
  }, [aggregationsQuery?.data?.aggregations, evidenceIds]);

  useEffect(() => {
    setPriority(item?.priority || "Unassigned");
    const currentPriorityIndex = requestPriorities.findIndex(
      (r: any) => r.key.toLowerCase() === item?.priority?.toLowerCase()
    );
    if (currentPriorityIndex === -1) {
      setPriority("Unassigned");
    }
    setColor(requestPriorities[currentPriorityIndex]?.color);
  }, [item]);

  return (
    <Flex h={32}  align="center" w='100%' maw='100%'>
      <Flex h={32} mr={4} align="center" maw='100%'>
        <Stack mr={9} justify="center" align="center">
          {sourceOfAssignment === "suggestion" ? (
            <Tooltip label="Found by Bagel AI">
              <Stack h={20} w={20} align="center">
                <BagelAiIcon />
              </Stack>
            </Tooltip>
          ) : (sourceOfAssignment === "bagel" || sourceOfAssignment === "search") ? (
            <Tooltip label="Added by PM">
              <Stack align="center" h={20} w={20}>
                <FromBagelIcon />
              </Stack>
            </Tooltip>
          ) : (sourceOfAssignment === "request") ? (
            <Tooltip label="Request">
              <Stack align="center" h={20} w={20}>
                <RequestIcon />
              </Stack>
            </Tooltip>
          ) : (
            ""
          )}
        </Stack>
        {origin && (
          <Tooltip label={capitalizeFirstLetter(origin) || "N/A"}>
            <Stack
              mx={6}
              align="center"
              mb={origin === "hero" ? "3px" : origin === "facebook" ? -5: "0px"}
            >
              <SourceIcon 
                width={22}
                height={22} sourceName={origin?.toLowerCase() || "N/A"}
              />
            </Stack>
          </Tooltip>
        )}

        <Tooltip label={item?.isContainer && item?.hasChildren ? `${item?.customersCount} CUSTOMERS` :companyName || "N/A"}>
          <Box h={40} px={9} sx={{ borderRight: "1px solid #D8D8DB" }}  w='100%' >
            <Text h={16} fz={12} fw={400}>
              Customer
            </Text>
            <Text fz={12} fw={600} lineClamp={1} sx={{ height: 'auto'}} >
              {
                item?.isContainer && item?.hasChildren ? 
                  <Text
                    bg="#5C5CEB1A"
                    color="#5C5CEB"
                    fz="10px"
                    fw={600}
                    lineClamp={1}
                    sx={{ lineHeight: "20px", borderRadius: '4px' }}
                    px={4}
                    py={2}
                  >
                    {item?.customersCount} CUSTOMERS
                  </Text> :
                  (companyName || "N/A")
              }
            </Text>
          </Box>
        </Tooltip>
      </Flex>
      <Flex>
        <Flex>
          {impactsToShow &&
            impactsToShow.length > 0 &&
            impactsToShow.map((impactItem: any) => (
              <Box
                h={40}
                px={9}
                maw={124}
                sx={{ borderRight: "1px solid #D8D8DB" }}
                key={impactItem?.label}
              >
                <Tooltip label={impactItem?.label || "N/A"}>
                  <Text h={16} fz={12} fw={400} lineClamp={1}>
                    {impactItem?.label || "N/A"}
                  </Text>
                </Tooltip>

                {item?.isContainer && evidenceAggregations ? 
                  <Text h={16} fz={12} fw={600} lineClamp={1}>
                    {impactItem.propertyName
                      ? formatBigMonetaryValueIdeas(
                        evidenceAggregations[impactItem.propertyName]['total'],
                        (impactItem?.impact?.prefix || undefined)
                      )
                      : "N/A"}
                  </Text> : 
                  <Text h={16} fz={12} fw={600} lineClamp={1}>
                    {impactItem.fieldPath &&
                    getNestedValue(item, impactItem.fieldPath)
                      ? formatBigMonetaryValueIdeas(
                        getNestedValue(item, impactItem.fieldPath),
                        (impactItem?.impact?.prefix || undefined)
                      )
                      : "N/A"}
                  </Text>}
              </Box>
            ))}
        </Flex>
     
        <Box mr={45}>
          <Menu>
            <Menu.Target>
              <Tooltip label={`Priority: ${priority || "N/A"}`}>
                <Box sx={{ cursor: "pointer" }}>
                  <Stack align="center" justify="center" ml={16} fz="14px">
                    {" "}
                    <IconAlertOctagon size={16} color={color} />{" "}
                  </Stack>
                </Box>
              </Tooltip>
            </Menu.Target>

            <Menu.Dropdown>
              {requestPriorities.map((priorityItem: any) => (
                <Menu.Item
                  w="180px"
                  key={priorityItem.label}
                  onClick={() => {
                    setPriority(priorityItem?.label);
                    setColor(priorityItem.color);
                    onSelectSave("priority", priorityItem.label);
                  }}
                >
                  {priorityItem.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Box>
      </Flex>
      
    </Flex>
  );
};

export default CardAttributes;
